import React, { useState } from "react";
import styled from "@emotion/styled";
import { Switch, Button, Checkbox, TextField, MenuItem } from "@mui/material";
import CircleCheckedFilled from "@mui/icons-material/CheckCircle";
import CircleUnchecked from "@mui/icons-material/RadioButtonUnchecked";
import { StyledDropdown } from "./edit-tags";
import Dropdown from "./dropdown";
import { UserWizardObj } from "../types";
import { saveUserWizard } from "../firebase";
import { useNavigate } from "react-router-dom";
import { RootState } from "../redux/root-store";
import { connect } from "react-redux";

const Title = styled.span`
    font-size: 2rem;
    font-weight: 500;
    line-height: 1.5rem;
`;

const Row = styled.div`
    display: flex;
    align-items: center;
`;

const CheckboxRow = styled(Row)`
    border-radius: 15px;
    width: 100%;
    border: 1px solid #ccc;
    padding: 0rem 1rem;
    justify-content: space-between;
    cursor: pointer;
    margin-top: 1rem;
`;

const Column = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const RedUnderline = styled("span")(
    ({ theme }: any) => `
    color: ${theme.palette.primary.main};
    text-decoration: underline;
    cursor: pointer;
    width: 8rem;
    display: block;
    text-align: center;
    white-space: nowrap;
`
);

const Text = styled.span``;

const mapStateToProps = (state: RootState) => ({
    loggedUser: state.session.user,
});

interface Props extends ReturnType<typeof mapStateToProps> {
    userObject: UserWizardObj;
    setUserObject: (data: UserWizardObj) => void;
}

const UserWizard = (props: Props) => {
    const { userObject, setUserObject } = props;
    const [step, setStep] = useState<number>(0);
    const navigate = useNavigate();

    const handleSaveWizard = async () => {
        if (!props.loggedUser) return;
        await saveUserWizard(props.loggedUser.user_id, userObject);
        navigate("/contacts");
    };

    return (
        <>
            <Row
                style={{
                    justifyContent: "space-between",
                    marginBottom: "1rem",
                }}
            >
                <Button
                    variant="outlined"
                    color="grey2"
                    onClick={() => {
                        if (step > 0) setStep(step - 1);
                    }}
                >
                    &lt;
                </Button>
                <Button
                    disabled={step >= 2}
                    variant="outlined"
                    color="grey2"
                    onClick={() => {
                        setStep(step + 1);
                    }}
                >
                    Skip
                </Button>
            </Row>
            {step === 0 && (
                <>
                    <Title>Sync Contacts</Title>
                    <Text>
                        By syncing your contacts to one centralized place,
                        you'll unlock the full potential of NetworkNerd.
                    </Text>
                    <Row>
                        <Switch
                            checked={userObject.syncContacts || false}
                            onChange={() => {
                                setUserObject({
                                    ...userObject,
                                    syncContacts: !userObject.syncContacts,
                                });
                            }}
                        />
                        <Text style={{ fontSize: "0.8rem" }}>
                            Automatically add new NetworkNerd Contacts to my
                            iPhone
                        </Text>
                    </Row>

                    <div style={{ width: "100%", marginTop: "1rem" }}>
                        <Button
                            disabled
                            variant="outlined"
                            color="grey2"
                            style={{ width: "100%" }}
                        >
                            Sync Apple Contacts
                        </Button>
                        <Text
                            style={{
                                textAlign: "center",
                                width: "100%",
                                fontSize: "0.8rem",
                            }}
                        >
                            Import Apple contacts from your iPhone
                        </Text>
                    </div>

                    <div style={{ width: "100%", marginTop: "1rem" }}>
                        <Button
                            disabled
                            variant="outlined"
                            color="grey2"
                            style={{ width: "100%" }}
                        >
                            Add Google Calendar Contacts
                        </Button>
                        <Text
                            style={{
                                textAlign: "center",
                                width: "100%",
                                fontSize: "0.8rem",
                            }}
                        >
                            Import Google contacts from your google account
                        </Text>
                    </div>

                    <div style={{ width: "100%", marginTop: "1rem" }}>
                        <Button
                            variant="outlined"
                            color="grey2"
                            style={{ width: "100%" }}
                            onClick={() => {
                                const mailAnchor = document.createElement("a");
                                mailAnchor.href = `mailto:admin@networknerd.app?subject=Request for NetworkNerd Data Import Support&body=Please describe the data you'd like to get into your account. %0D%0A%0D%0AFor NetworkNerd Internal Use%0D%0AUserID: ${props.loggedUser?.user_id}%0D%0A Current Plan: ${props.loggedUser?.purchase_name}%0D%0AAccount email: ${props.loggedUser?.email}`;
                                mailAnchor.click();
                            }}
                        >
                            Custom Contact Import
                        </Button>
                        <Text
                            style={{
                                textAlign: "center",
                                width: "100%",
                                fontSize: "0.8rem",
                            }}
                        >
                            Ask NetworkNerd to import a custom contact list form
                            Outlook, LinkedIn, spreadsheet, etc...
                        </Text>
                    </div>
                    <Button
                        variant="outlined"
                        color="grey2"
                        style={{
                            width: "100%",
                            marginTop: "2rem",
                            marginBottom: "2rem",
                        }}
                        onClick={() => setStep(step + 1)}
                    >
                        Continue
                    </Button>
                </>
            )}
            {step === 1 && (
                <>
                    <Title style={{ marginBottom: "1rem" }}>
                        Add some close contacts to get started
                    </Title>
                    {[
                        ...userObject.initialContacts,
                        { name: "", tags: [], reminder: "Quarterly" },
                    ].map((initialContact: any, index: number) => (
                        <Row
                            key={index}
                            style={{
                                marginBottom: "1rem",
                                width: "100%",
                                flexWrap: "wrap",
                                paddingBottom: "1rem",
                                borderBottom: "1px solid #ccc",
                            }}
                        >
                            <TextField
                                label="Contact Name"
                                style={{ flex: 1, minWidth: "10rem" }}
                                value={initialContact.name}
                                onChange={(
                                    evt: React.ChangeEvent<HTMLInputElement>
                                ) => {
                                    if (
                                        index ===
                                        userObject.initialContacts.length
                                    ) {
                                        setUserObject({
                                            ...userObject,
                                            initialContacts: [
                                                ...userObject.initialContacts,
                                                {
                                                    name: evt.target.value,
                                                    tags: [],
                                                    reminder: "Quarterly",
                                                },
                                            ],
                                        });
                                    } else {
                                        setUserObject({
                                            ...userObject,
                                            initialContacts:
                                                userObject.initialContacts.map(
                                                    (
                                                        oldContact: any,
                                                        idx: number
                                                    ) =>
                                                        idx === index
                                                            ? {
                                                                  ...initialContact,
                                                                  name: evt
                                                                      .target
                                                                      .value,
                                                              }
                                                            : oldContact
                                                ),
                                        });
                                    }
                                }}
                            />
                            <Row>
                                <StyledDropdown
                                    style={{
                                        maxWidth: "calc(100% - 8rem)",
                                    }}
                                    multiple
                                    select
                                    placeholder="Add groups"
                                    value={initialContact.tags}
                                    options={[
                                        {
                                            text: "Friends",
                                            value: "Friends",
                                            key: "Friends",
                                        },
                                        {
                                            text: "Family",
                                            value: "Family",
                                            key: "Family",
                                        },
                                        {
                                            text: "Professional",
                                            value: "Professional",
                                            key: "Professional",
                                        },
                                    ]}
                                    onChange={(
                                        evt: React.SyntheticEvent,
                                        data: any
                                    ) => {
                                        setUserObject({
                                            ...userObject,
                                            initialContacts:
                                                userObject.initialContacts.map(
                                                    (
                                                        oldContact: any,
                                                        idx: number
                                                    ) =>
                                                        idx === index
                                                            ? {
                                                                  ...initialContact,
                                                                  tags: data.value,
                                                              }
                                                            : oldContact
                                                ),
                                        });
                                    }}
                                />
                                <Column>
                                    <span>Reminder</span>
                                    <Dropdown
                                        options={
                                            <>
                                                {[
                                                    "Daily",
                                                    "Weekly",
                                                    "Bi-Weekly",
                                                    "Monthly",
                                                    "Quarterly",
                                                    "Half-Yearly",
                                                    "Yearly",
                                                    "Once in a While",
                                                    "Do Not",
                                                ].map((option: string) => (
                                                    <MenuItem
                                                        key={option}
                                                        onClick={() => {
                                                            setUserObject({
                                                                ...userObject,
                                                                initialContacts:
                                                                    userObject.initialContacts.map(
                                                                        (
                                                                            oldContact: any,
                                                                            idx: number
                                                                        ) =>
                                                                            idx ===
                                                                            index
                                                                                ? {
                                                                                      ...initialContact,
                                                                                      reminder:
                                                                                          option,
                                                                                  }
                                                                                : oldContact
                                                                    ),
                                                            });
                                                        }}
                                                    >
                                                        {option}
                                                    </MenuItem>
                                                ))}
                                            </>
                                        }
                                    >
                                        <RedUnderline>
                                            {initialContact.reminder}
                                        </RedUnderline>
                                    </Dropdown>
                                </Column>
                            </Row>
                        </Row>
                    ))}
                    <Button
                        variant="outlined"
                        color="grey2"
                        style={{
                            width: "100%",
                            marginTop: "2rem",
                            marginBottom: "2rem",
                        }}
                        onClick={() => setStep(step + 1)}
                    >
                        Continue
                    </Button>
                </>
            )}
            {step === 2 && (
                <>
                    <Title>Notifications & Location Sharing</Title>
                    <Text>
                        Notifications & location sharing will unlock features
                        like smart reminders and alerts while near a friend. And
                        don't worry, your data isn't shared and we don't send
                        messages or any private information.
                    </Text>
                    <div style={{ width: "100%" }}>
                        <CheckboxRow
                            onClick={() => {
                                setUserObject({
                                    ...userObject,
                                    mobileNotifications:
                                        !userObject.mobileNotifications,
                                });
                            }}
                        >
                            <Text>Mobile Notifications</Text>
                            <Checkbox
                                checked={
                                    userObject.mobileNotifications || false
                                }
                                onChange={() => {}}
                                icon={<CircleUnchecked />}
                                checkedIcon={<CircleCheckedFilled />}
                            />
                        </CheckboxRow>
                        <Text
                            style={{
                                textAlign: "center",
                                width: "100%",
                                fontSize: "0.8rem",
                            }}
                        >
                            Mobile notifications work only if you have the
                            mobile application
                        </Text>
                    </div>
                    <CheckboxRow
                        onClick={() => {
                            setUserObject({
                                ...userObject,
                                emailNotifications:
                                    !userObject.emailNotifications,
                            });
                        }}
                    >
                        <Text>Email Notifications</Text>
                        <Checkbox
                            checked={userObject.emailNotifications || false}
                            onChange={() => {}}
                            icon={<CircleUnchecked />}
                            checkedIcon={<CircleCheckedFilled />}
                        />
                    </CheckboxRow>
                    <div style={{ width: "100%" }}>
                        <CheckboxRow
                            onClick={() => {
                                setUserObject({
                                    ...userObject,
                                    locationPermission:
                                        !userObject.locationPermission,
                                });
                            }}
                        >
                            <Text>Location Permission</Text>
                            <Checkbox
                                checked={userObject.locationPermission || false}
                                onChange={() => {}}
                                icon={<CircleUnchecked />}
                                checkedIcon={<CircleCheckedFilled />}
                            />
                        </CheckboxRow>
                        <Text
                            style={{
                                textAlign: "center",
                                width: "100%",
                                fontSize: "0.8rem",
                            }}
                        >
                            Mobile notifications work only if you have the
                            mobile application
                        </Text>
                    </div>
                    <Button
                        variant="outlined"
                        style={{
                            width: "100%",
                            marginTop: "2rem",
                            marginBottom: "2rem",
                        }}
                        onClick={handleSaveWizard}
                    >
                        Continue
                    </Button>
                </>
            )}
        </>
    );
};

export default connect(mapStateToProps, {})(UserWizard);
